import { useLanguage, type Locale, type TranslateFn } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { RequestRevision } from '@/api/types';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * Request revisions (ADR-089), the warehouse's side: the client changed a request after it was sent to the warehouse.
 * A notice at the top of the inbound / order page with what the LAST revision changed (previous -> new) and the full
 * list of revisions. It carries request fields and quantities only; the receiving / picking panels stay as they are.
 */
const LINE_KEYS = { ADDED: 'revNotice.line.ADDED', REMOVED: 'revNotice.line.REMOVED', CHANGED: 'revNotice.line.CHANGED' } as const;
const FIELD_KEYS: Record<string, TranslationKey> = {
  reference: 'revNotice.f.reference',
  expectedAt: 'revNotice.f.expectedAt',
  dueAt: 'revNotice.f.dueAt',
  'shipTo.name': 'revNotice.f.shipTo.name',
  'shipTo.phone': 'revNotice.f.shipTo.phone',
  'shipTo.email': 'revNotice.f.shipTo.email',
  'shipTo.address': 'revNotice.f.shipTo.address',
  'shipTo.city': 'revNotice.f.shipTo.city',
  'shipTo.postalCode': 'revNotice.f.shipTo.postalCode',
  'shipTo.country': 'revNotice.f.shipTo.country',
  'shipTo.comment': 'revNotice.f.shipTo.comment',
};
const DATE_FIELDS = new Set(['expectedAt', 'dueAt']);

export function revisionLines(t: TranslateFn, locale: Locale, diff: RequestRevision['diff'] | null | undefined): string[] {
  if (!diff) return [];
  const out: string[] = [];
  for (const l of diff.lines ?? []) {
    out.push(t(LINE_KEYS[l.op], { sku: l.skuCode, from: formatNumber(l.from ?? 0, locale), to: formatNumber(l.to ?? 0, locale) }));
  }
  for (const f of diff.fields ?? []) {
    const show = (v: string | null): string => (v === null || v === '' ? '—' : DATE_FIELDS.has(f.field) ? formatDateTime(v, locale) : v);
    const label = FIELD_KEYS[f.field];
    out.push(t('revNotice.fieldChange', { field: label ? t(label) : f.field, from: show(f.from), to: show(f.to) }));
  }
  return out;
}

/** The small "edited" marker for a list row. */
export function RevisionMark({ revisionNo }: { readonly revisionNo: number | undefined }): JSX.Element | null {
  const { t } = useLanguage();
  if ((revisionNo ?? 1) <= 1) return null;
  return <span className="revision-mark" title={t('revNotice.markTitle')}>{t('revNotice.mark')}</span>;
}

export function RequestRevisionNotice({
  kind,
  revisionNo,
  revisions,
}: {
  readonly kind: 'INBOUND' | 'OUTBOUND';
  readonly revisionNo: number | undefined;
  readonly revisions: readonly RequestRevision[] | undefined;
}): JSX.Element | null {
  const { t, locale } = useLanguage();
  const list = revisions ?? [];
  if ((revisionNo ?? 1) <= 1 || list.length === 0) return null;
  const latest = list[0]!;
  const who = latest.actor_name ? ` · ${latest.actor_name}` : '';
  return (
    <div className="card revision-notice" role="status" data-testid="request-revision-notice">
      <h2 className="card-title">{t('revNotice.title', { n: latest.revision_no, when: formatDateTime(latest.created_at, locale) })}{who}</h2>
      <p className="sub">{kind === 'INBOUND' ? t('revNotice.hintInbound') : t('revNotice.hintOrder')}</p>
      <ul className="revision-lines" data-testid="revision-latest">
        {revisionLines(t, locale, latest.diff).map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      {list.length > 1 && (
        <details className="revision-history" data-testid="revision-all">
          <summary>{t('revNotice.all', { n: list.length })}</summary>
          <ol>
            {list.map((r) => (
              <li key={r.revision_no}>
                <div className="rev-head">
                  {t('revNotice.rev', { n: r.revision_no })} · {formatDateTime(r.created_at, locale)}{r.revision_no > 1 && r.actor_name ? ` · ${r.actor_name}` : ''}
                </div>
                {r.revision_no === 1 ? (
                  <div className="rev-line">{t('revNotice.submitted')}</div>
                ) : (
                  <ul>
                    {revisionLines(t, locale, r.diff).map((line, i) => (
                      <li key={i} className="rev-line">{line}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
